import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isRecoverablePropertyPhotoUrl,
  recoverLegacyPropertyPhoto,
} from './propertyPhoto';
import { classifyStoredPhoto } from './storagePaths';
import { signStorageRead } from './signStorageObject';

function revokeUrl(url) {
  if (url && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(url);
  }
}

function immediateSrc(located) {
  if (located.kind === 'external' || located.kind === 'signed') return located.src;
  return '';
}

/**
 * src is empty until a stored photo can be shown.
 * An external image URL is used as-is. A property-photos object is private,
 * so the hook asks for a signed URL first. A signed object that the image
 * element cannot decode is fetched once and, when it is a multipart upload
 * body, replaced with the image inside it.
 */
export default function usePropertyPhotoDisplay(stored) {
  const located = classifyStoredPhoto(stored, 'property-photos');
  const initial = immediateSrc(located);
  const [displaySrc, setDisplaySrc] = useState(initial);
  const [phase, setPhase] = useState(() => {
    if (located.kind === 'empty') return 'missing';
    if (located.kind === 'object') return 'recovering';
    return initial ? 'ready' : 'missing';
  });
  const blobRef = useRef('');
  const genRef = useRef(0);
  const phaseRef = useRef(phase);
  const sourceRef = useRef(initial);
  const displayRef = useRef(initial);
  const recoveringRef = useRef(false);
  phaseRef.current = phase;
  displayRef.current = displaySrc;

  useEffect(() => {
    const gen = genRef.current + 1;
    genRef.current = gen;
    recoveringRef.current = false;
    revokeUrl(blobRef.current);
    blobRef.current = '';

    if (located.kind === 'empty') {
      sourceRef.current = '';
      setDisplaySrc('');
      setPhase('missing');
      return undefined;
    }
    if (located.kind === 'external' || located.kind === 'signed') {
      sourceRef.current = located.src;
      setDisplaySrc(located.src);
      setPhase('ready');
      return undefined;
    }

    sourceRef.current = '';
    setDisplaySrc('');
    setPhase('recovering');
    signStorageRead('property-photos', stored).then((url) => {
      if (genRef.current !== gen) return;
      if (!url) {
        setPhase('missing');
        return;
      }
      sourceRef.current = url;
      setDisplaySrc(url);
      setPhase('ready');
    });
    return undefined;
  }, [stored, located.kind, located.src, located.path]);

  useEffect(() => () => {
    genRef.current += 1;
    revokeUrl(blobRef.current);
  }, []);

  const onError = useCallback(() => {
    if (phaseRef.current === 'missing') return;
    const source = sourceRef.current;
    if (displayRef.current && displayRef.current !== source) {
      setPhase('missing');
      return;
    }
    if (recoveringRef.current) return;
    if (!isRecoverablePropertyPhotoUrl(source)) {
      setPhase('missing');
      return;
    }
    const gen = genRef.current;
    recoveringRef.current = true;
    setPhase('recovering');
    recoverLegacyPropertyPhoto(source).then((blobUrl) => {
      if (genRef.current !== gen) {
        revokeUrl(blobUrl);
        return;
      }
      recoveringRef.current = false;
      if (!blobUrl) {
        setPhase('missing');
        return;
      }
      blobRef.current = blobUrl;
      setDisplaySrc(blobUrl);
      setPhase('ready');
    });
  }, []);

  return {
    src: phase === 'ready' ? displaySrc : '',
    missing: phase === 'missing',
    recovering: phase === 'recovering',
    onError,
  };
}
